import React from 'react';
import { C } from '../../theme.js';
import { FieldLabel } from '../../components/Primitives.jsx';
import { SOURCE_COLUMNS, outputNameIssue } from '../../data.js';
import { ColumnsContext } from '../StepInspector.jsx';
import { MultiCols } from '../shared/MultiCols.jsx';
import { OutputNameField } from '../shared/OutputNameField.jsx';
import { SourceSelect } from '../shared/SourceSelect.jsx';
import { sourceColumns } from '../shared/sourceColumns.js';

// Same Source-anchored contract as Case When/Filter/Compute: when Source
// isn't self, columns are dropped from that Source's own rows, self is left
// unchanged for later steps, and the result is only reachable through
// Output Name.
export function DropColumnsInspector({ step, onChange, columns, pipeline, runMap, secondaryDatasets, sheets, onBrowseJoinFile }) {
  const ctxCols = React.useContext(ColumnsContext);
  const c = step.config || {};
  const sourceIsSelf = !c.source || c.source === '__self__';
  const liveCols = columns || ctxCols || SOURCE_COLUMNS;
  const cols = sourceIsSelf ? liveCols : (sourceColumns(c.source, { sheets, secondaryDatasets, pipeline, runMap }) || liveCols);
  const outputNameIssueRes = outputNameIssue(c.output_name, { sheets, secondaryDatasets, steps: pipeline?.steps, stepId: step.id });
  const outputNameMissing = !sourceIsSelf && !(c.output_name || '').trim();
  const sel = c.columns || [];
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <FieldLabel hint="Dataset to drop columns from — an uploaded sheet, an earlier published step, or this block's own data">Source</FieldLabel>
        <SourceSelect
          value={c.source}
          onChange={(v) => u({ source: v })}
          secondaryDatasets={secondaryDatasets}
          sheets={sheets}
          pipeline={pipeline}
          currentStep={step}
          onBrowse={onBrowseJoinFile ? () => onBrowseJoinFile(step.id) : undefined}
          buildTargetName={pipeline?.inputFile?.buildTargetName}
        />
      </div>

      <FieldLabel>Columns to drop</FieldLabel>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px' }}>
        Selected columns are removed from every downstream step. Reorder is
        applied after drop.
      </div>
      <MultiCols columns={cols} selected={sel} onChange={(v) => u({ columns: v })} />

      <OutputNameField
        value={c.output_name}
        onChange={(v) => u({ output_name: v })}
        required={outputNameMissing}
        hint={sourceIsSelf
          ? 'Optional — also publish this result under a name so later blocks can pick it as their Source'
          : "Required — Source isn't self, so this step's result doesn't touch the live pipeline payload; without a name it isn't reachable anywhere"}
        warningMessage={<>Source is &ldquo;{c.source}&rdquo;, not self — without an Output Name this step&rsquo;s result goes nowhere: the live pipeline payload stays unchanged and the rows aren&rsquo;t saved anywhere.</>}
        placeholder="e.g., cleaned"
        issue={outputNameIssueRes}
      />
    </div>
  );
}
